import React from 'react';

import styled from '@emotion/styled';

import { FormSubmitButton } from '../../../components/Button/FormSubmitButton';
import BundleCollapseGroup from '../../../components/FormCom/BundleCollapseGroup';
import useSetPrice from '../../../hooks/dashboard/revenue/useSetPrice';
import { LookupPriceSchema } from '../../../models/dashboard/revenue/lookup-price';
import { createPriceManagementActions } from '../../../redux/slices/priceManagementSlice';
import { useAppDisPatch, useAppSelector } from '../../../redux/store';
import BundlesForm from './BundlesForm/BundlesForm';
import PayAsYouGoForm from './PayAsYouGoForm';

const ManagePriceForm = () => {
  const dispatch = useAppDisPatch();

  const [payAsYouGoResetKey, setPayAsYouGoResetKey] = React.useState(0);

  const { pricingBundle, payAsYouGoPrice } = useAppSelector(
    (state) => state.priceManagementData,
  );

  const { currentConfigData } = useAppSelector(
    (state) => state.currentConfigData,
  );

  const { setPrice, loading } = useSetPrice();

  // --- Effective price per lookup ---
  // Precedence: valid new entry > existing saved price > 0 (no price)
  const payAsYouGoPriceNum = Number(payAsYouGoPrice);
  const isPayAsYouGoProvided = !!payAsYouGoPrice && payAsYouGoPrice !== '';
  const isPayAsYouGoPriceValid =
    isPayAsYouGoProvided &&
    LookupPriceSchema.isValidSync({ price: payAsYouGoPrice });
  const effectivePricePerLookup = isPayAsYouGoPriceValid
    ? payAsYouGoPriceNum
    : (currentConfigData?.unitPrice ?? 0);

  // --- Bundle validation helpers ---
  type BundleItem = (typeof pricingBundle)[0];

  // An empty bundle has no user input at all — it should be silently ignored.
  const isBundleEmpty = (b: BundleItem) => !b.numberOfLookups && !b.discount;

  // A bundle is fully valid when all required fields are present and pass rules.
  const isBundleValid = (b: BundleItem) => {
    const numLookups = Number(b.numberOfLookups);
    const discountNum = Number(b.discount);
    const computedOriginalPrice = numLookups * effectivePricePerLookup;
    return (
      numLookups > 0 &&
      Number.isInteger(numLookups) &&
      !Number.isNaN(discountNum) &&
      discountNum >= 0 &&
      discountNum <= 100 &&
      computedOriginalPrice > 0
    );
  };

  // A partial bundle has some data but is not yet valid — it must block submission.
  const isBundlePartial = (b: BundleItem) =>
    !isBundleEmpty(b) && !isBundleValid(b);

  const hasPartialBundle = pricingBundle.some(isBundlePartial);
  const hasValidBundle = pricingBundle.some(isBundleValid);

  // At least one valid pricing config must exist to allow submission.
  const hasAtLeastOneValidConfig =
    (isPayAsYouGoProvided && isPayAsYouGoPriceValid) || hasValidBundle;

  const isSubmitDisabled =
    loading ||
    (isPayAsYouGoProvided && !isPayAsYouGoPriceValid) || // entered but invalid
    hasPartialBundle || // incomplete bundle present
    !hasAtLeastOneValidConfig; // nothing valid at all

  const onSubmit = () => {
    // Only include bundles that are fully valid. Empty bundles are ignored.
    const validBundlePayload = pricingBundle.filter(isBundleValid).map((b) => {
      const numLookups = Number(b.numberOfLookups);
      const discountNum = Number(b.discount);
      const originalPriceNum = numLookups * effectivePricePerLookup;
      const newPriceNum = originalPriceNum * (1 - discountNum / 100);
      return {
        numberOfLookups: b.numberOfLookups,
        originalPrice: String(originalPriceNum),
        newPrice: String(newPriceNum),
        discount: String(discountNum),
      };
    });

    const payload = {
      // Include unitPrice: prefer a newly entered valid price; fall back to the
      // existing saved unit price so bundles always have a price context.
      ...(isPayAsYouGoProvided && isPayAsYouGoPriceValid
        ? { unitPrice: payAsYouGoPrice }
        : validBundlePayload.length > 0 && currentConfigData?.unitPrice != null
          ? { unitPrice: String(currentConfigData.unitPrice) }
          : {}),
      ...(validBundlePayload.length > 0
        ? { pricingBundle: validBundlePayload }
        : {}),
    };

    setPrice(payload, () => {
      dispatch(createPriceManagementActions.reset());
    });
  };

  return (
    <Container>
      <PayAsYouGoForm key={payAsYouGoResetKey} />

      {/*  */}
      <BundleCollapseGroup title='Set Bundle Price'>
        <BundlesForm />
      </BundleCollapseGroup>

      {/*  */}
      <FormButtonArea>
        <FormSubmitButton
          className='clear fit large-side-padding'
          onClick={() => {
            dispatch(createPriceManagementActions.reset());
            setPayAsYouGoResetKey((prev) => prev + 1);
          }}
        >
          Cancel
        </FormSubmitButton>
        <FormSubmitButton
          className='fit large-side-padding'
          loading={loading}
          disabled={isSubmitDisabled}
          btnType='button'
          onClick={onSubmit}
        >
          Save Changes
        </FormSubmitButton>
      </FormButtonArea>
    </Container>
  );
};

export default ManagePriceForm;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 24px;
  border-radius: 24px;
`;

const FormButtonArea = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  background: rgba(91, 145, 245, 0.02);
  padding: 24px 0px;
`;
